import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Ban,
  CalendarClock,
  Download,
  Flag,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { SelectField } from '../components/ui/Field';
import { GatedSection } from '../components/shared/GatedSection';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { RelationPanel } from '../components/shared/RelationPanel';
import {
  AttemptReviewDrawer,
  AttemptStatusChip,
  AudienceChip,
  canCancelRun,
  PassFailChip,
  RunEditDrawer,
  RunStatusChip,
  type AssessmentAttemptStatus,
  type RunEditDrawerMode,
  type RunEligibilityResponse,
  type RunResultsResponse,
  type RunDetail,
} from '../components/assessments';

type AttemptSummary = RunResultsResponse['attempts'][number];

const RUN_EDIT_ROLES: ReadonlySet<string> = new Set([
  'super_admin',
  'academic_admin',
  'hr',
  'nalim',
]);

export const Route = createFileRoute('/_authed/academics/assessments/runs/$runId')({
  component: RunDetailPage,
});

type Tab = 'attempts' | 'stats' | 'audience' | 'export' | 'activity';

const TAB_OPTIONS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'attempts', label: 'Attempts' },
  { value: 'stats', label: 'Question stats' },
  { value: 'audience', label: 'Audience' },
  { value: 'export', label: 'Export' },
  { value: 'activity', label: 'Activity' },
];

function RunDetailPage() {
  const { runId } = Route.useParams();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('attempts');
  const [editDrawer, setEditDrawer] = useState<RunEditDrawerMode>(null);
  const [reviewAttempt, setReviewAttempt] = useState<AttemptSummary | null>(
    null,
  );

  const runQuery = useQuery({
    queryKey: ['assessments', 'run', runId] as const,
    queryFn: ({ signal }) =>
      apiRequest<RunDetail>(
        `/api/assessments/runs/${encodeURIComponent(runId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const resultsQuery = useQuery({
    queryKey: ['assessments', 'run-results', runId] as const,
    queryFn: ({ signal }) =>
      apiRequest<RunResultsResponse>(
        `/api/assessments/runs/${encodeURIComponent(runId)}/results`,
        { signal },
      ),
    enabled: Boolean(runQuery.data),
  });

  const run = runQuery.data;
  const results = resultsQuery.data;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['assessments', 'run', runId] });
    void queryClient.invalidateQueries({ queryKey: ['assessments', 'runs'] });
    void queryClient.invalidateQueries({
      queryKey: ['assessments', 'run-results', runId],
    });
  };

  // Derived counters for the expanded 6-stat KPI strip. `In progress` and
  // `Flagged` are computed client-side from the per-attempt projection
  // already served by `/results`, so no extra fetch is needed.
  const inProgressCount = useMemo(
    () =>
      results?.attempts.filter((a) => a.status === 'in_progress').length ?? 0,
    [results],
  );
  const flaggedCount = useMemo(
    () =>
      results?.attempts.filter((a) => a.flaggedForReview === true).length ?? 0,
    [results],
  );

  return (
    <ListShell
      title={run?.title ?? 'Run'}
      description={run ? `Run id: ${run.id.slice(0, 8)}…` : undefined}
      primaryAction={
        <Link
          to="/academics/assessments"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to tests & quizzes
        </Link>
      }
    >
      <GatedSection
        error={runQuery.error}
        title="Run"
        onRetry={() => runQuery.refetch()}
      >
        {runQuery.isLoading || !run ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <RunSummary
              run={run}
              attemptsCount={results?.attempts.length ?? 0}
              inProgressCount={inProgressCount}
              flaggedCount={flaggedCount}
              onChanged={invalidate}
              onEditEligibility={() =>
                setEditDrawer({ kind: 'eligibility', run })
              }
              onReschedule={() => setEditDrawer({ kind: 'reschedule', run })}
            />

            <div
              role="tablist"
              aria-label="Run sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TAB_OPTIONS.map((opt) => {
                const active = tab === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(opt.value)}
                    className={clsx(
                      'rounded-t-md px-4 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {tab === 'attempts' ? (
              <AttemptsTab
                results={results}
                isLoading={resultsQuery.isLoading}
                onSelect={(a) => setReviewAttempt(a)}
              />
            ) : null}
            {tab === 'stats' ? (
              <StatsTab results={results} isLoading={resultsQuery.isLoading} />
            ) : null}
            {tab === 'audience' ? (
              <AudienceTab
                run={run}
                onManageEligibility={() =>
                  setEditDrawer({ kind: 'eligibility', run })
                }
              />
            ) : null}
            {tab === 'export' ? <ExportTab runId={runId} runTitle={run.title} /> : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="assessment_run"
                targetId={runId}
                title="Activity"
                emptyCopy="No run activity recorded yet."
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      <RunEditDrawer
        mode={editDrawer}
        onClose={() => setEditDrawer(null)}
        onSaved={() => {
          setEditDrawer(null);
          invalidate();
        }}
      />

      <AttemptReviewDrawer
        attempt={reviewAttempt}
        runId={runId}
        onClose={() => setReviewAttempt(null)}
        onChanged={invalidate}
      />
    </ListShell>
  );
}

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

interface RunSummaryProps {
  run: RunDetail;
  attemptsCount: number;
  inProgressCount: number;
  flaggedCount: number;
  onChanged: () => void;
  onEditEligibility: () => void;
  onReschedule: () => void;
}

function RunSummary({
  run,
  attemptsCount,
  inProgressCount,
  flaggedCount,
  onChanged,
  onEditEligibility,
  onReschedule,
}: RunSummaryProps) {
  const role = useAuthStore((s) => s.user?.primaryRole);
  const { toast } = useToast();
  const { confirm } = useConfirm();

  // We cannot derive scope without the template; the cancel matrix on the
  // server checks RUN_UNPUBLISH_ROLES (super_admin, academic_admin, hr, nalim).
  // Mirror that here.
  const isUnpublisher = role !== undefined && RUN_EDIT_ROLES.has(role);
  const canCancel = canCancelRun(run.status) && isUnpublisher;
  const canEdit = canCancelRun(run.status) && isUnpublisher;
  const canEditEligibility =
    canEdit && run.audienceType === 'selected_users';

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ id: string; status: string }>(
        `/api/assessments/runs/${encodeURIComponent(run.id)}/cancel`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Run cancelled. In-progress attempts were finalised.', {
        variant: 'success',
      });
      onChanged();
    },
    onError: (err: unknown) => {
      toast(
        err instanceof ApiError ? err.message : 'Could not cancel run.',
        { variant: 'error' },
      );
    },
  });

  const onCancel = async () => {
    const ok = await confirm({
      title: 'Cancel this run?',
      description:
        'In-progress attempts are auto-submitted with their current answers. This cannot be undone.',
      confirmLabel: 'Cancel run',
      variant: 'danger',
    });
    if (!ok) return;
    cancelMutation.mutate();
  };

  return (
    <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl font-semibold">{run.title}</h2>
            <RunStatusChip status={run.status} />
            <AudienceChip type={run.audienceType} />
            {run.periodKey ? (
              <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 font-mono text-[10px] text-[color:var(--color-text-muted)]">
                {run.periodKey}
              </span>
            ) : null}
          </div>
          <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
            Template:{' '}
            <Link
              to="/academics/assessments/$templateId"
              params={{ templateId: run.templateId }}
              className="text-gold-700 underline"
            >
              View template
            </Link>
          </p>
          <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
            Opens {run.opensAt ? formatDate(run.opensAt, { withTime: true }) : '—'}
            {' · '}
            Closes {run.closesAt ? formatDate(run.closesAt, { withTime: true }) : '—'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {canEditEligibility ? (
            <Button
              variant="secondary"
              size="sm"
              icon={<Users size={14} aria-hidden="true" />}
              onClick={onEditEligibility}
            >
              Edit eligibility
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              variant="secondary"
              size="sm"
              icon={<CalendarClock size={14} aria-hidden="true" />}
              onClick={onReschedule}
            >
              Reschedule
            </Button>
          ) : null}
          {canCancel ? (
            <Button
              variant="ghost"
              size="sm"
              icon={<Ban size={14} aria-hidden="true" />}
              onClick={onCancel}
              loading={cancelMutation.isPending}
              className="text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10"
            >
              Cancel run
            </Button>
          ) : null}
        </div>
      </div>

      {run.createdByName ? (
        <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
          Created by {run.createdByName}
        </p>
      ) : null}

      <dl className="mt-5 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Attempts" value={`${attemptsCount}`} />
        <Stat label="In progress" value={`${inProgressCount}`} />
        <Stat
          label="Submitted"
          value={`${run.stats?.submittedAttempts ?? 0}`}
        />
        <Stat
          label="Flagged"
          value={`${flaggedCount}`}
          tone={flaggedCount > 0 ? 'danger' : undefined}
        />
        <Stat
          label="Avg score"
          value={
            run.stats?.averageScorePercent !== null &&
            run.stats?.averageScorePercent !== undefined
              ? `${run.stats.averageScorePercent}%`
              : '—'
          }
        />
        <Stat
          label="Pass threshold"
          value={
            run.passThresholdPercent !== null
              ? `${run.passThresholdPercent}%`
              : 'Template default'
          }
        />
      </dl>
    </section>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'danger' | undefined;
}) {
  return (
    <div
      className={clsx(
        'rounded-md border bg-[color:var(--color-surface)] px-3 py-2',
        tone === 'danger'
          ? 'border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/5'
          : 'border-[color:var(--color-border)]',
      )}
    >
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd
        className={clsx(
          'mt-0.5 font-display text-xl font-bold',
          tone === 'danger'
            ? 'text-[color:var(--color-danger)]'
            : 'text-[color:var(--color-text)]',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attempts tab
// ---------------------------------------------------------------------------

function AttemptsTab({
  results,
  isLoading,
  onSelect,
}: {
  results: RunResultsResponse | undefined;
  isLoading: boolean;
  onSelect: (attempt: AttemptSummary) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<'all' | AssessmentAttemptStatus>(
    'all',
  );
  const [passFilter, setPassFilter] = useState<'all' | 'pass' | 'fail'>('all');

  if (isLoading || !results) {
    return <Skeleton className="h-72 w-full" />;
  }
  const attempts = results.attempts.filter((a) => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (passFilter === 'pass' && a.pass !== true) return false;
    if (passFilter === 'fail' && a.pass !== false) return false;
    return true;
  });

  return (
    <section className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <SelectField
          label="Status"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as 'all' | AssessmentAttemptStatus)
          }
          options={[
            { value: 'all', label: 'All' },
            { value: 'in_progress', label: 'In progress' },
            { value: 'submitted', label: 'Submitted' },
            { value: 'expired', label: 'Expired' },
            { value: 'void', label: 'Void' },
          ]}
        />
        <SelectField
          label="Pass / fail"
          value={passFilter}
          onChange={(e) => setPassFilter(e.target.value as typeof passFilter)}
          options={[
            { value: 'all', label: 'All' },
            { value: 'pass', label: 'Passed' },
            { value: 'fail', label: 'Failed' },
          ]}
        />
      </div>

      {attempts.length === 0 ? (
        <EmptyState
          title="No attempts match the filter"
          description="Adjust the filters or wait for users to start the run."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--color-surface)]">
                <tr>
                  <Th>User</Th>
                  <Th>Started</Th>
                  <Th>Status</Th>
                  <Th align="right">Score</Th>
                  <Th>Result</Th>
                  <Th>Flags</Th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((a) => (
                  <tr
                    key={a.attemptId}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect(a)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(a);
                      }
                    }}
                    className="cursor-pointer border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)] focus-visible:bg-[color:var(--color-surface)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-gold-500"
                  >
                    <td className="px-4 py-2 align-middle">
                      <p className="font-medium text-[color:var(--color-text)]">
                        {a.studentName ?? a.userId.slice(0, 8) + '…'}
                      </p>
                      {a.admissionNumber ? (
                        <p className="font-mono text-xs text-[color:var(--color-text-muted)]">
                          {a.admissionNumber}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                      {a.startedAt ? formatDate(a.startedAt, { withTime: true }) : '—'}
                    </td>
                    <td className="px-4 py-2 align-middle">
                      <AttemptStatusChip status={a.status} />
                    </td>
                    <td className="px-4 py-2 align-middle text-right tabular-nums">
                      {a.scorePercent !== null ? `${a.scorePercent}%` : '—'}
                    </td>
                    <td className="px-4 py-2 align-middle">
                      <PassFailChip pass={a.pass} />
                    </td>
                    <td className="px-4 py-2 align-middle">
                      <div className="flex flex-wrap items-center gap-1">
                        {a.autoSubmitted ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-xs text-[color:var(--color-text-muted)]">
                            Auto-submitted
                          </span>
                        ) : null}
                        {a.flaggedForReview ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/10 px-2 py-0.5 text-xs font-medium text-[color:var(--color-danger)]">
                            <Flag size={10} aria-hidden="true" /> Flagged
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Stats tab
// ---------------------------------------------------------------------------

function StatsTab({
  results,
  isLoading,
}: {
  results: RunResultsResponse | undefined;
  isLoading: boolean;
}) {
  if (isLoading || !results) return <Skeleton className="h-64 w-full" />;
  const sorted = [...results.perQuestion].sort(
    (a, b) => (a.accuracyPercent ?? -1) - (b.accuracyPercent ?? -1),
  );
  if (sorted.length === 0) {
    return (
      <EmptyState
        title="No per-question stats yet"
        description="Stats populate once attempts are submitted."
      />
    );
  }

  return (
    <section>
      <p className="mb-3 text-xs text-[color:var(--color-text-muted)]">
        Sorted by accuracy (lowest first) — the lowest rows are the hardest
        questions for this audience.
      </p>
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)]">
            <tr>
              <Th>Question</Th>
              <Th align="right">Attempts</Th>
              <Th align="right">Correct</Th>
              <Th align="right">Accuracy</Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((q) => {
              const pct = q.accuracyPercent;
              const tone =
                pct === null
                  ? 'text-[color:var(--color-text-muted)]'
                  : pct < 40
                    ? 'text-[color:var(--color-danger)]'
                    : pct < 70
                      ? 'text-gold-700'
                      : 'text-[color:var(--color-success)]';
              return (
                <tr
                  key={q.questionId}
                  className="border-t border-[color:var(--color-border)]"
                >
                  <td className="px-4 py-2 align-middle font-mono text-xs text-[color:var(--color-text-muted)]">
                    {q.questionId.slice(0, 12)}…
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums">
                    {q.attempts}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums">
                    {q.correct}
                  </td>
                  <td
                    className={clsx(
                      'px-4 py-2 align-middle text-right font-semibold tabular-nums',
                      tone,
                    )}
                  >
                    {pct !== null ? `${pct}%` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Audience tab
// ---------------------------------------------------------------------------

function AudienceTab({
  run,
  onManageEligibility,
}: {
  run: RunDetail;
  onManageEligibility: () => void;
}): JSX.Element {
  const role = useAuthStore((s) => s.user?.primaryRole);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const canManage =
    canCancelRun(run.status) &&
    run.audienceType === 'selected_users' &&
    role !== undefined &&
    RUN_EDIT_ROLES.has(role);

  const eligibilityQuery = useQuery({
    queryKey: ['assessments', 'run-eligibility', run.id] as const,
    queryFn: ({ signal }) =>
      apiRequest<RunEligibilityResponse>(
        `/api/assessments/runs/${encodeURIComponent(run.id)}/eligibility`,
        { signal },
      ),
    retry: (failureCount, err) =>
      err instanceof ApiError && err.status === 404 ? false : failureCount < 1,
  });

  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const current = eligibilityQuery.data?.items ?? [];
      const audienceUserIds = current
        .map((it) => it.userId)
        .filter((id) => id !== userId);
      if (audienceUserIds.length === 0) {
        throw new Error(
          'Cannot remove the last eligible user — cancel the run instead.',
        );
      }
      return apiRequest<{ id: string; status: string }>(
        `/api/assessments/runs/${encodeURIComponent(run.id)}`,
        { method: 'PATCH', body: { audienceUserIds } },
      );
    },
    onSuccess: () => {
      toast('User removed from eligibility roster.', { variant: 'success' });
      void queryClient.invalidateQueries({
        queryKey: ['assessments', 'run-eligibility', run.id],
      });
      void queryClient.invalidateQueries({
        queryKey: ['assessments', 'run', run.id],
      });
    },
    onError: (err: unknown) => {
      toast(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not update eligibility.',
        { variant: 'error' },
      );
    },
  });

  const items = eligibilityQuery.data?.items ?? [];
  const explanation = eligibilityQuery.data?.explanation;

  const handleRemove = async (userId: string, label: string) => {
    const ok = await confirm({
      title: 'Remove from this run?',
      description: `${label} will no longer be able to start new attempts. Any in-progress attempt will run to completion.`,
      confirmLabel: 'Remove',
      variant: 'danger',
    });
    if (!ok) return;
    removeMutation.mutate(userId);
  };

  const errorState =
    eligibilityQuery.isError && !eligibilityQuery.data
      ? {
          message:
            eligibilityQuery.error instanceof ApiError
              ? eligibilityQuery.error.message
              : 'Could not load eligibility.',
          onRetry: () => {
            void eligibilityQuery.refetch();
          },
        }
      : null;

  const action = canManage ? (
    <Button
      size="sm"
      variant="secondary"
      icon={<Users size={14} aria-hidden="true" />}
      onClick={onManageEligibility}
    >
      Edit eligibility
    </Button>
  ) : null;

  return (
    <RelationPanel
      title="Audience"
      description={
        run.audienceType === 'selected_users'
          ? 'Users explicitly enrolled in this run.'
          : run.audienceType === 'batch'
            ? 'Batch enrolment — every active student in the linked batch is eligible.'
            : 'Candidate list — managed by the hiring module.'
      }
      count={items.length}
      action={action}
      loading={eligibilityQuery.isLoading && !eligibilityQuery.data}
      error={errorState}
      // Only the `selected_users` audience renders a true "empty" state.
      // Batch and candidate_list audiences are non-enumerable from this
      // view; we want their explanation/link children to render rather
      // than RelationPanel's EmptyState replacement.
      isEmpty={
        run.audienceType === 'selected_users' &&
        !eligibilityQuery.isLoading &&
        items.length === 0
      }
      empty={{
        copy: 'No users on the roster yet.',
      }}
    >
      {run.audienceType === 'batch' && run.audienceRefId ? (
        <p className="mb-3 text-xs text-[color:var(--color-text-muted)]">
          See full enrollment on the{' '}
          <Link
            to="/academics/batches"
            className="text-gold-700 underline"
          >
            batches list
          </Link>{' '}
          (id <span className="font-mono">{run.audienceRefId}</span>).
        </p>
      ) : null}
      {explanation ? (
        <p className="mb-3 text-xs text-[color:var(--color-text-muted)]">
          {explanation}
        </p>
      ) : null}
      {items.length > 0 ? (
        <div className="overflow-hidden rounded-md border border-[color:var(--color-border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-surface)]">
              <tr>
                <Th>User</Th>
                <Th>Role</Th>
                {canManage ? <Th align="right">Actions</Th> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const label =
                  it.displayName?.trim() ||
                  it.studentName?.trim() ||
                  it.email?.trim() ||
                  it.userId;
                return (
                  <tr
                    key={it.userId}
                    className="border-t border-[color:var(--color-border)]"
                  >
                    <td className="px-4 py-2 align-middle">
                      <p className="font-medium text-[color:var(--color-text)]">
                        {label}
                      </p>
                      {it.email ? (
                        <p className="text-xs text-[color:var(--color-text-muted)]">
                          {it.email}
                        </p>
                      ) : null}
                      {it.admissionNumber ? (
                        <p className="font-mono text-[11px] text-[color:var(--color-text-muted)]">
                          {it.admissionNumber}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 align-middle text-xs capitalize text-[color:var(--color-text-muted)]">
                      {it.primaryRole?.replace(/_/g, ' ') ?? '—'}
                    </td>
                    {canManage ? (
                      <td className="px-4 py-2 align-middle text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void handleRemove(it.userId, label)}
                          loading={
                            removeMutation.isPending &&
                            removeMutation.variables === it.userId
                          }
                          className="text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10"
                        >
                          Remove
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </RelationPanel>
  );
}

// ---------------------------------------------------------------------------
// Export tab
// ---------------------------------------------------------------------------

function ExportTab({ runId, runTitle }: { runId: string; runTitle: string }) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const onDownload = async () => {
    setLoading(true);
    try {
      const apiOrigin =
        (import.meta.env.VITE_API_ORIGIN as string | undefined) ??
        'https://api.babulilmonline.com';
      // We use a raw fetch so we can pull text/csv directly.
      const headers: Record<string, string> = {};
      const token = window.sessionStorage.getItem('bbl-session-token');
      if (token) headers['authorization'] = `Bearer ${token}`;
      const url = new URL(
        `/api/assessments/runs/${encodeURIComponent(runId)}/results`,
        apiOrigin,
      );
      url.searchParams.set('format', 'csv');
      const res = await fetch(url.toString(), {
        credentials: 'include',
        headers,
      });
      if (!res.ok) throw new Error('Failed to download');
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `${runTitle.replace(/\W+/g, '-').toLowerCase()}-results.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dlUrl);
      toast('Download started.', { variant: 'success' });
    } catch (err) {
      toast(
        err instanceof Error ? err.message : 'Could not download CSV.',
        { variant: 'error' },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="space-y-4 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-6">
      <header>
        <h3 className="font-display text-lg font-semibold">Export results</h3>
        <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
          Download a CSV with every attempt's score, status, and integrity
          flags. Useful for offline review or to share with the academy office.
        </p>
      </header>
      <Button
        icon={<Download size={16} aria-hidden="true" />}
        onClick={onDownload}
        loading={loading}
      >
        Download attempts CSV
      </Button>
    </section>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={clsx(
        'px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

